import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { specsEditorActions } from "../../common/slices/specsEditor/slice";
import type { OutputImage, SpecsEditorSession, SpecsLayer } from "./types";

type UseSpecsEditorMasksArgs = {
  session: SpecsEditorSession;
  layers: SpecsLayer[];
};

export function useSpecsEditorMasks({
  session,
  layers,
}: UseSpecsEditorMasksArgs) {
  const dispatch = useDispatch();

  const pendingDetectsRef = useRef(new Set<string>());
  const completedDetectsRef = useRef(new Set<string>());

  useEffect(() => {
    const maskFilterTypes = ["alphaToMask", "whiteToMask", "blackToMask"];

    const detectForImage = async (
      img: {
        aiTransform: OutputImage["aiTransform"];
        transformations: OutputImage["transformations"];
        sourceLayerId: string | null;
      },
      imageId: string,
      side: string
    ) => {
      if (img.transformations.some((t) => maskFilterTypes.includes(t.type))) {
        return;
      }

      const inputSource = side === "back" ? session.backInput : session.input;
      const sampleUrl =
        img.aiTransform?.acceptedImageUrl ??
        inputSource?.layers.find((layer) => layer.id === img.sourceLayerId)
          ?.imageDataUrl;
      if (!sampleUrl) return;

      const key = `${imageId}:${side}`;
      if (pendingDetectsRef.current.has(key)) return;
      if (completedDetectsRef.current.has(key)) return;
      pendingDetectsRef.current.add(key);

      try {
        const { detectMaskFilter } = await import("./detectMaskFilter");
        const filterType = await detectMaskFilter(sampleUrl);
        if (
          filterType === "alphaToMask" ||
          filterType === "whiteToMask" ||
          filterType === "blackToMask"
        ) {
          dispatch(
            specsEditorActions.setAutoMaskFilter({
              imageId,
              filterType,
              side,
            })
          );
        }
      } finally {
        pendingDetectsRef.current.delete(key);
        completedDetectsRef.current.add(key);
      }
    };

    for (const layer of layers) {
      const mask = layer.mask;
      if (!mask) continue;
      if (mask.aiTransform?.acceptedImageUrl || mask.sourceLayerId) {
        detectForImage(mask, mask.id, "front");
      }
      if (
        mask.back &&
        (mask.back.aiTransform?.acceptedImageUrl || mask.back.sourceLayerId)
      ) {
        detectForImage(mask.back, mask.id, "back");
      }
    }
  }, [dispatch, layers, session.backInput, session.input]);
}
